import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FieldHeader, FieldHelpText } from './FieldParts';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

const UrlInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    defaultValue?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'URL';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Enter URL...';
  const required = data?.required || false;
  const helpText = data?.helpText;

  // Read-only (e.g. a completed result): render the entered URL as a
  // non-editable value — linked when it's a safe http(s) URL, otherwise
  // plain text.
  if (isReadOnly) {
    const url = (displayValue || '').trim();
    const isHttpUrl = /^https?:\/\//i.test(url);
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {url ? (
            isHttpUrl ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                {url}
              </a>
            ) : (
              url
            )
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      {/* https:// prefix addon (library spec). The stored value keeps the
          full URL; only the part after the protocol is edited/shown. */}
      <div className="flex items-stretch overflow-hidden rounded-[var(--forms-input-radius)] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white transition-[border-color,box-shadow] focus-within:border-[var(--forms-accent,var(--forms-accent-default))] focus-within:shadow-[0_0_0_3px_var(--forms-accent-ring,var(--forms-accent-ring-default))]">
        <span className="flex flex-none items-center border-r border-[var(--forms-border-light)] bg-[#f7f9fb] px-[12px] text-[13px] font-semibold text-[var(--forms-muted-soft)]">
          https://
        </span>
        <input
          id={element.id}
          type="text"
          inputMode="url"
          placeholder={placeholder}
          value={(displayValue || '').replace(/^https?:\/\//i, '')}
          onChange={(e) => {
            const rest = e.target.value.replace(/^https?:\/\//i, '');
            setValue(rest ? `https://${rest}` : '');
          }}
          className="min-w-0 flex-1 bg-transparent px-[var(--forms-input-px)] py-[var(--forms-input-py)] text-[length:var(--forms-input-font)] leading-[1.2] text-[var(--forms-ink)] placeholder:text-[var(--forms-faint)] focus:outline-none"
        />
      </div>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default UrlInput;
